import {
  apiCommodityJointWriteOffCommodityDetail,
  ApiCommodityJointWriteOffCommodityDetailResponse,
  apiCommodityJointWriteOffSkuDetail,
  apiCommodityWriteOffCommodityMarketingDetail,
  ApiCommodityWriteOffCommodityMarketingDetailResponse,
  apiEvaluationEvaluateGetLatestEvaluateList,
  ApiEvaluationEvaluateGetLatestEvaluateListResponse,
} from '@/api'
import { AsyncReturnType, RequiredDeep } from 'vtils/types'
import { createModelPlus, prepareData, requiredDeep } from '@/utils'
import { defineProvider } from '@/hooks'
import { Ref } from '@vue/composition-api'

export interface RawJointGoodsModel
  extends RequiredDeep<ApiCommodityJointWriteOffCommodityDetailResponse> {
  /** 商品ID */
  goodsId: string

  /** 是否已下架 */
  isOff: boolean

  /** 店铺是否休息中 */
  isRest: boolean

  /** 是否已停业 */
  isClosed: boolean

  /** 是否代金券 */
  isCoupon: boolean

  /** 是否日期库存 */
  isDateStock: boolean

  /** 是否售罄 */
  isSold: boolean

  /** 评价数量 */
  commentCount: number

  /** 评价星级 */
  commentStar: number

  /** 评价列表 */
  commentList: RequiredDeep<ApiEvaluationEvaluateGetLatestEvaluateListResponse>['list']

  /** 营销数据 */
  marketingData: RequiredDeep<ApiCommodityWriteOffCommodityMarketingDetailResponse>

  /** 分享参数 */
  shareParams: any

  refreshMarketing: () => Promise<void>
}

export type JointGoodsModel = AsyncReturnType<typeof getJointGoodsModel>

export async function getJointGoodsModel(goodsId: string) {
  const { goodsData } = await prepareData({
    goodsData: apiCommodityJointWriteOffCommodityDetail(
      {
        commodityId: goodsId,
      },
      {
        noErrorToast: true,
      },
    )
      .then(requiredDeep)
      .catch(
        () =>
          ({} as RequiredDeep<ApiCommodityJointWriteOffCommodityDetailResponse>),
      ),
  })

  if (!goodsData.commodityId) {
    return createModelPlus<RawJointGoodsModel>({
      isOff: true,
    } as any)
  }

  const { commentData, marketingData, stockData } = await prepareData({
    commentData: apiEvaluationEvaluateGetLatestEvaluateList({
      commodityServiceId: goodsId,
      pageIndex: 1,
      pageSize: 3,
    }).then(requiredDeep),
    marketingData: apiCommodityWriteOffCommodityMarketingDetail({
      commodityId: goodsId,
      skuId: goodsId,
    }).then(requiredDeep),
    stockData: apiCommodityJointWriteOffSkuDetail({
      skuId: goodsId,
      provinceCode: '',
      areaCode: '',
      cityCode: '',
    }).then(requiredDeep),
  })

  const jointGoodsModel = createModelPlus<RawJointGoodsModel>({
    ...{},
    ...goodsData,
    get goodsId() {
      return goodsId
    },
    get isOff() {
      return false
    },
    get isRest() {
      return goodsData.storeStatus === 4
    },
    get isClosed() {
      return false
    },
    get isCoupon() {
      return false
    },
    get isDateStock() {
      return false
    },
    get isSold() {
      return stockData.surplusStock === 0
    },
    get commentCount() {
      return commentData.totalNum
    },
    get commentStar() {
      return commentData.evaluateStat
    },
    get commentList() {
      return commentData.list
    },
    get marketingData() {
      return marketingData
    },
    get shareParams() {
      return {
        title: this.commodityTitle,
        desc: this.commoditySubTitle,
        cover: this.coverPic,
      }
    },
    async refreshMarketing() {
      this.marketingData = await apiCommodityWriteOffCommodityMarketingDetail({
        commodityId: goodsId,
        skuId: goodsId,
      }).then(requiredDeep)
    },
  })

  return jointGoodsModel
}

export const jointGoodsModelProvider = defineProvider<Ref<JointGoodsModel>>()
